import { useState, useRef, useCallback } from "react";
import { publicTurnAudioUrl, publicWordAudioUrl } from "../publicShareApi";

/**
 * usePublicTTS — TTS playback for the anonymous shared-topic page.
 *
 * Slim sibling of useTTS: same playTurn/playAll/stop surface plus playWord,
 * but sources audio from the public per-URL audio/mpeg endpoints (browser
 * HTTP cache, no base64) and never touches auth or progression endpoints —
 * playAll's onComplete only feeds the page's local play counter.
 */
export function usePublicTTS(token) {
  const [playingTurnId, setPlayingTurnId] = useState(null);
  const [loadingTurnId, setLoadingTurnId] = useState(null);
  const [playingWordId, setPlayingWordId] = useState(null);

  const audioRef = useRef(null);
  const cancelRef = useRef(false);
  // Populated by the scenario renderer: { [turn.id]: DOM element }
  const turnRefsRef = useRef({});

  const stop = useCallback(() => {
    cancelRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingTurnId(null);
    setLoadingTurnId(null);
    setPlayingWordId(null);
  }, []);

  const _playUrl = useCallback(async (url) => {
    const audio = new Audio(url);
    audioRef.current = audio;
    await new Promise((resolve) => {
      audio.onended = resolve;
      audio.onerror = resolve;
      audio.play().catch(resolve);
    });
  }, []);

  const _playOneTurn = useCallback(
    async (turn) => {
      setLoadingTurnId(turn.id);
      setPlayingTurnId(null);
      const audio = new Audio(publicTurnAudioUrl(token, turn.id));
      audioRef.current = audio;
      await new Promise((resolve) => {
        audio.oncanplay = resolve;
        audio.onerror = resolve;
        audio.load();
      });
      if (cancelRef.current) return;
      setLoadingTurnId(null);
      setPlayingTurnId(turn.id);
      await new Promise((resolve) => {
        audio.onended = resolve;
        audio.onerror = resolve;
        audio.play().catch(resolve);
      });
      if (!cancelRef.current) setPlayingTurnId(null);
    },
    [token]
  );

  const playTurn = useCallback(
    async (turn) => {
      stop();
      cancelRef.current = false;
      try {
        await _playOneTurn(turn);
      } catch {
        setLoadingTurnId(null);
        setPlayingTurnId(null);
      }
    },
    [stop, _playOneTurn]
  );

  const playWord = useCallback(
    async (word) => {
      stop();
      cancelRef.current = false;
      setPlayingWordId(word.id);
      try {
        await _playUrl(publicWordAudioUrl(token, word.id));
      } finally {
        if (!cancelRef.current) setPlayingWordId(null);
      }
    },
    [stop, _playUrl, token]
  );

  const playAll = useCallback(
    async (turns, { onComplete, autoScroll = true } = {}) => {
      stop();
      cancelRef.current = false;

      let completed = false;
      try {
        for (const turn of turns) {
          if (cancelRef.current) break;

          // Same scroll behaviour as useTTS: page-level scrollIntoView, or the
          // nearest scrollable ancestor when the page itself shouldn't move.
          const el = turnRefsRef.current[turn.id];
          if (el) {
            if (autoScroll) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
            } else {
              let p = el.parentElement;
              while (p) {
                const oy = getComputedStyle(p).overflowY;
                if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight) break;
                p = p.parentElement;
              }
              if (p) {
                const er = el.getBoundingClientRect();
                const pr = p.getBoundingClientRect();
                const top = p.scrollTop + (er.top - pr.top) - p.clientHeight / 2 + er.height / 2;
                p.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
              }
            }
          }

          try {
            await _playOneTurn(turn);
          } catch {
            setLoadingTurnId(null);
            setPlayingTurnId(null);
            return;
          }

          if (cancelRef.current) break;
        }

        if (!cancelRef.current) {
          setPlayingTurnId(null);
          setLoadingTurnId(null);
          completed = turns.length > 0;
        }
      } finally {
        if (completed && typeof onComplete === "function") {
          try {
            onComplete();
          } catch {
            /* swallow callback errors */
          }
        }
      }
    },
    [stop, _playOneTurn]
  );

  return {
    playingTurnId,
    loadingTurnId,
    playingWordId,
    playTurn,
    playAll,
    playWord,
    stop,
    turnRefsRef,
  };
}
